import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Divider,
  Stack,
  Typography,
  Chip,
  Collapse,
  Box,
} from "@mui/material";
import { INVOICE_TABLE_HEADERS } from "../../constant/TableHeaders";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendToMobileOutlinedIcon from "@mui/icons-material/SendToMobileOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import PayInvoiceDialog from "./PayInvoiceDialog";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { downloadInvoice, getInvoicePayments } from "../../services/api/invoice";
import {
  StyledTableBody,
  TableBodyRow,
  TableHeaderRow,
} from "../../styles/TableStyles";
import { INDIAN_RUPEE_SIGN } from "../../constant/constant";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";
import ViewInvoiceDialog from "./ViewInvoiceDialog";
import { getUserDetails } from "../../services/api/user";
import { sendWhatsAppMessage } from "../../utils/sendWhatsAppMessage";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";

const InvoiceTable = ({ rows }) => {
  const dispatch = useDispatch();

  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [openRow, setOpenRow] = useState(null); // Track the row open state
  const [userDetails, setUserDetails] = useState([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user_response = await getUserDetails();
        setUserDetails(user_response.data);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchUserDetails();
  }, [dispatch]);

  const handleViewClick = (invoice) => {
    setSelectedInvoice(invoice);
    setOpenViewDialog(true);
  };

  const handleViewDialogClose = () => {
    setOpenViewDialog(false);
    setSelectedInvoice(null);
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setOpenDialog(true);
  };

  const fetchPayments = async (invoiceId) => {
    try {
      const paymentData = await getInvoicePayments(invoiceId); // Fetch payments for this invoice
      // Sort payments in descending order of `id`
      const sortedPayments = [...paymentData.data].sort((a, b) => a.id - b.id);
      setPayments(sortedPayments); // Assuming response contains payment data
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const handleRowClick = (invoice) => {
    // If the selected row is clicked again, toggle the open state
    if (openRow === invoice.id) {
      setOpenRow(null); // Close the row if it's already open
    } else {
      setSelectedInvoice(invoice);
      setOpenRow(invoice.id); // Open the selected row
      fetchPayments(invoice.id); // Fetch payments when row is expanded
    }
  };

  const handleSendToPhone = (invoice) => {
    sendWhatsAppMessage({
      invoice,
      userDetails,
      type: "rentInvoiceReminder",
      showToast: (msg) => dispatch(showToast(msg)),
    });
  };

  /* ---------------- DOWNLOAD / PAY ---------------- */
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      setDownloadingInvoiceId(invoiceId);
      const response = await downloadInvoice(invoiceId);

      // Create PDF Blob
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      console.log(response.headers);
      let fileName = `Invoice_${invoiceId}.pdf`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Failed to download invoice");
      dispatch(showToast({ message: error.message, severity: "error" }));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableHeaderRow>
              {INVOICE_TABLE_HEADERS.map((item, index) => (
                <TableCell
                  key={index}
                  align={item.alignItemTo}
                  sx={{ width: item.width }}
                >
                  {item.title}
                </TableCell>
              ))}
            </TableHeaderRow>
          </TableHead>

          <StyledTableBody>
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                {/* Main Row */}
                <TableBodyRow
                  hover
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  {/* Expand Icon */}
                  <TableCell sx={{ width: 50, textAlign: "left" }}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleRowClick(row)}
                    >
                      {openRow === row.id ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  {/* Invoice Date + ID */}
                  <TableCell sx={{ width: 200, textAlign: "left" }}>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {new Date(row.invoice_date).toLocaleDateString("en-GB")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Invoice_{row.invoice_no}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Tenant Name + Contact */}
                  <TableCell sx={{ width: 210, textAlign: "left" }}>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {`${row.tenant_first_name} ${row.tenant_last_name}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        <SmartphoneOutlinedIcon
                          fontSize="small"
                          sx={{ marginRight: 0.5 }}
                        />
                        {row.tenant_contact}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Rent Amount */}
                  <TableCell sx={{ width: 150, textAlign: "left" }}>
                    {`${INDIAN_RUPEE_SIGN} ${parseFloat(
                      row.rent_amount
                    ).toFixed(2)}`}
                  </TableCell>

                  {/* Overdue Amount */}
                  <TableCell
                    sx={{
                      width: 150,
                      textAlign: "left",
                      color: row.overdue_amount > 0 ? "#DC143C" : "#4caf50",
                    }}
                  >
                    {`${INDIAN_RUPEE_SIGN} ${parseFloat(
                      row.overdue_amount
                    ).toFixed(2)}`}
                  </TableCell>

                  {/* Invoice Amount */}
                  <TableCell sx={{ width: 190, textAlign: "left" }}>
                    {`${INDIAN_RUPEE_SIGN} ${parseFloat(
                      row.invoice_amount
                    ).toFixed(2)}`}
                  </TableCell>

                  {/* Paid Amount */}
                  <TableCell sx={{ width: 150, textAlign: "left" }}>
                    {`${INDIAN_RUPEE_SIGN} ${parseFloat(
                      row.paid_amount
                    ).toFixed(2)}`}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={row.invoice_status}
                      size="small"
                      variant="outlined"
                      sx={{
                        width: 130,
                        height: 35,
                        borderColor:
                          row.invoice_status === "PAID"
                            ? "#4caf50"
                            : row.invoice_status === "UNPAID"
                              ? "#DC143C"
                              : "#e8961aff",
                        color:
                          row.invoice_status === "PAID"
                            ? "#4caf50"
                            : row.invoice_status === "UNPAID"
                              ? "#DC143C"
                              : "#e8961aff",
                        fontWeight: "500",
                        textTransform: "capitalize",
                      }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ width: 150, textAlign: "left" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      divider={<Divider orientation="vertical" flexItem />}
                    >
                      <Tooltip title="Pay Invoice">
                        <IconButton
                          size="small"
                          onClick={() => handlePayInvoice(row)}
                        >
                          <PointOfSaleOutlinedIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Download Invoice">
                        <span>
                          <IconButton
                            size="small"
                            disabled={downloadingInvoiceId === row.id}
                            onClick={() => handleDownloadInvoice(row.id)}
                          >
                            {downloadingInvoiceId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <FileDownloadOutlinedIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="View">
                        <IconButton
                          onClick={() => handleViewClick(row)}
                          size="small"
                        >
                          <VisibilityOutlinedIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Send to Phone">
                        <IconButton
                          size="small"
                          onClick={() => handleSendToPhone(row)}
                        >
                          <SendToMobileOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableBodyRow>

                {/* Nested Row - Collapse */}
                {openRow === row.id && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      sx={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Collapse in={true} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            Payment History
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Transaction Type</TableCell>
                                <TableCell>Reference Number</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payments.map((payment, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {new Date(
                                      payment.payment_date
                                    ).toLocaleDateString("en-GB")}
                                  </TableCell>
                                  <TableCell>
                                    ₹{" "}
                                    {parseFloat(payment.paid_amount).toFixed(2)}
                                  </TableCell>
                                  <TableCell>{payment.payment_method}</TableCell>
                                  <TableCell>{payment?.reference_number ? payment.reference_number : "-"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </StyledTableBody>
        </Table>
      </TableContainer>

      {/* Dialogs and Snackbar */}
      <PayInvoiceDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        invoiceId={selectedInvoice?.id}
        invoiceAmount={selectedInvoice?.invoice_amount}
        rentAmount={selectedInvoice?.rent_amount}
      />

      <ViewInvoiceDialog
        open={openViewDialog}
        onClose={handleViewDialogClose}
        invoice={selectedInvoice}
      />
    </>
  );
};

export default InvoiceTable;
